/** Fleet routes (IA §2 Manage › Fleet, §3 entity pages). Every view's state is in the URL. */
import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { str } from '@/lib/search';

export interface VehiclesSearch {
  q?: string;
  groups?: string;
  type?: string;
  status?: string;
  sort?: string;
  selected?: string;
}

export interface DriversSearch {
  q?: string;
  groups?: string;
  status?: string;
  sort?: string;
  selected?: string;
}

export interface DevicesSearch {
  q?: string;
  profile?: string;
  status?: string;
  linked?: 'yes' | 'no';
  sort?: string;
  selected?: string;
}

export interface GroupsSearch {
  group?: string;
  applies?: 'vehicle' | 'driver' | 'asset';
}

export interface EntitySearch {
  tab?: string;
}

export interface DeviceSearch extends EntitySearch {
  /** Messages tab: the message shown in the inspector (its time, ISO). */
  msg?: string;
}

const entitySearch = (s: Record<string, unknown>): EntitySearch => ({ tab: str(s.tab) });

export const fleetIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet',
  beforeLoad: () => {
    throw redirect({ to: '/fleet/vehicles' });
  },
});

export const vehiclesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet/vehicles',
  staticData: { permission: 'fleet.vehicle.read' },
  validateSearch: (s: Record<string, unknown>): VehiclesSearch => ({
    q: str(s.q),
    groups: str(s.groups),
    type: str(s.type),
    status: str(s.status),
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./vehicles/VehiclesPage')),
});

export const vehicleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/vehicles/$vehicleId',
  staticData: { permission: 'fleet.vehicle.read' },
  validateSearch: entitySearch,
  component: lazyRouteComponent(() => import('./vehicles/VehiclePage')),
});

export const driversRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet/drivers',
  staticData: { permission: 'fleet.driver.read' },
  validateSearch: (s: Record<string, unknown>): DriversSearch => ({
    q: str(s.q),
    groups: str(s.groups),
    status: str(s.status),
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./drivers/DriversPage')),
});

export const driverRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/drivers/$driverId',
  staticData: { permission: 'fleet.driver.read' },
  validateSearch: entitySearch,
  component: lazyRouteComponent(() => import('./drivers/DriverPage')),
});

export const devicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet/devices',
  staticData: { permission: 'fleet.device.read' },
  validateSearch: (s: Record<string, unknown>): DevicesSearch => ({
    q: str(s.q),
    profile: str(s.profile),
    status: str(s.status),
    linked: s.linked === 'yes' || s.linked === 'no' ? s.linked : undefined,
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./devices/DevicesPage')),
});

/** The installer persona lands on /devices. */
export const devicesAliasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/devices',
  beforeLoad: () => {
    throw redirect({ to: '/fleet/devices' });
  },
});

export const deviceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/devices/$deviceId',
  staticData: { permission: 'fleet.device.read' },
  validateSearch: (s: Record<string, unknown>): DeviceSearch => ({ tab: str(s.tab), msg: str(s.msg) }),
  component: lazyRouteComponent(() => import('./devices/DevicePage')),
});

export const groupsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet/groups',
  staticData: { permission: 'core.group.read' },
  validateSearch: (s: Record<string, unknown>): GroupsSearch => ({
    group: str(s.group),
    applies: s.applies === 'vehicle' || s.applies === 'driver' || s.applies === 'asset' ? s.applies : undefined,
  }),
  component: lazyRouteComponent(() => import('./groups/GroupsPage')),
});

/** Links from actions point at /fleet/groups/<id>; the groups page shows it selected. */
export const groupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet/groups/$groupId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/fleet/groups', search: { group: params.groupId } });
  },
});
